// Transform Page Logic
import { ImageTransformControlsManager } from './utils/image-transform-controls.js';
import {
  loadAuthenticatedMedia,
  loadAuthenticatedMediaAsBlob,
} from './utils/authenticated-media-loader.js';
import {
  getStoredTransformSource,
  clearStoredTransformSource,
} from './utils/transform-source.js';

// FileWrapper class to track file + transforms
class FileWrapper {
  file: File;
  previewUrl: string;
  transformManager: ImageTransformControlsManager | null = null;
  transformedBlob: Blob | null = null;
  previewElement: HTMLElement | null = null;
  imageElement: HTMLImageElement | null = null;

  constructor(file: File, previewUrl: string) {
    this.file = file;
    this.previewUrl = previewUrl;
  }

  initTransformControls(containerId: string, imageElement: HTMLImageElement) {
    this.imageElement = imageElement;
    this.transformManager = new ImageTransformControlsManager({
      containerId,
      imageElement,
      onTransformChange: (_transform, blob) => {
        this.transformedBlob = blob;
      },
    });
  }

  getFileForUpload(): File {
    // Return transformed blob as File if available, otherwise original
    if (this.transformedBlob) {
      return new File([this.transformedBlob], this.file.name, {
        type: 'image/png',
      });
    }
    return this.file;
  }

  destroy() {
    this.transformManager?.destroy();
    this.transformManager = null;
    this.transformedBlob = null;
    this.imageElement = null;
  }
}

// Global state
let selectedFiles: FileWrapper[] = [];
let nextFileId = 0;

export function initTransformPage() {
  const uploadArea = document.getElementById('uploadArea');
  const fileInput = document.getElementById('fileInput') as HTMLInputElement;
  const previewContainer = document.getElementById('previewContainer');
  const previewGrid = document.getElementById('previewGrid');
  const transformForm = document.getElementById('transformForm');
  const submitBtn = document.getElementById('submitBtn') as HTMLButtonElement;
  const message = document.getElementById('message');
  const resultsCard = document.getElementById('resultsCard');
  const resultsGrid = document.getElementById('resultsGrid');
  const batchCounter = document.getElementById('batchCounter');
  const providerSelect = document.getElementById(
    'provider'
  ) as HTMLSelectElement;
  const providerHint = document.getElementById('providerHint');
  const prefabPromptSelect = document.getElementById(
    'prefabPromptSelect'
  ) as HTMLSelectElement;
  const promptTextarea = document.getElementById(
    'prompt'
  ) as HTMLTextAreaElement;

  // Auto-detect API URL
  const API_URL = window.location.origin;

  // Load prefab prompts
  async function loadPrefabPrompts() {
    if (!prefabPromptSelect) return;

    try {
      const response = await (window as any).authenticatedFetch(
        `${API_URL}/api/prefab-prompts`
      );
      const prompts: any[] = await response.json();

      // Group by category
      const grouped: Record<string, any[]> = {};
      prompts.forEach((p) => {
        if (!grouped[p.category]) grouped[p.category] = [];
        grouped[p.category].push(p);
      });

      // Populate select
      Object.keys(grouped)
        .sort()
        .forEach((category) => {
          const optgroup = document.createElement('optgroup');
          optgroup.label = category;

          grouped[category].forEach((p) => {
            const option = document.createElement('option');
            option.value = p.prompt;
            option.textContent = p.name;
            optgroup.appendChild(option);
          });

          prefabPromptSelect.appendChild(optgroup);
        });

      // Handle selection
      prefabPromptSelect.addEventListener('change', () => {
        const selectedPrompt = prefabPromptSelect.value;
        if (selectedPrompt && promptTextarea) {
          // Append with newline if not empty, otherwise set
          if (promptTextarea.value.trim()) {
            promptTextarea.value = `${promptTextarea.value.trim()}\n${selectedPrompt}`;
          } else {
            promptTextarea.value = selectedPrompt;
          }
          // Reset select to default
          prefabPromptSelect.value = '';
        }
      });
    } catch (error) {
      console.error('Failed to load prefab prompts:', error);
    }
  }

  // Load prompts
  loadPrefabPrompts();

  // Check for sessionStorage data (from detail page re-transform)
  const stored = getStoredTransformSource();

  if (stored) {
    // Load from sessionStorage (detail page re-transform)
    loadImageFromUrl(stored.source!, stored.prompt, stored.sourceType);

    // Clear sessionStorage after loading
    clearStoredTransformSource();
  } else {
    // Check for URL parameters (legacy support)
    const urlParams = new URLSearchParams(window.location.search);
    const imageUrl = urlParams.get('imageUrl');
    const promptParam = urlParams.get('prompt');

    // Load image from URL if provided
    if (imageUrl && promptParam) {
      loadImageFromUrl(imageUrl, promptParam, 'url');
    }
  }

  // Function to load image from URL with source type indicator
  async function loadImageFromUrl(
    url: string,
    prompt: string,
    sourceType: string = 'url'
  ) {
    try {
      // Use authenticated fetch to get the image as blob directly
      const blob = await loadAuthenticatedMediaAsBlob(url);
      const filename = url.split('/').pop() || 'image.png';
      const file = new File([blob], filename, {
        type: blob.type || 'image/png',
      });

      // Set the prompt
      (document.getElementById('prompt') as HTMLTextAreaElement).value = prompt;

      // Handle the file
      handleFiles([file]);

      // Show success message with source type
      const sourceLabel =
        sourceType === 'source'
          ? 'source afbeelding'
          : sourceType === 'result'
            ? 'result afbeelding'
            : sourceType === 'input'
              ? 'input afbeelding'
              : 'afbeelding';
      showMessage(
        `✅ ${sourceLabel.charAt(0).toUpperCase() + sourceLabel.slice(1)} geladen uit geschiedenis`,
        'success'
      );
    } catch (error) {
      console.error('Failed to load image from URL:', error);
      showMessage(
        '⚠️ Kon afbeelding niet laden. Probeer handmatig uploaden.',
        'error'
      );
    }
  }

  // Provider info
  const providerInfo: Record<
    string,
    { hint: string; supportsImg2Img: boolean }
  > = {
    'gemini-direct': {
      hint: 'Direct Google AI Studio API • FREE img2img • 1500/dag limit',
      supportsImg2Img: true,
    },
    'gemini-image': {
      hint: 'Gemini via OpenRouter proxy • img2img • $0.001238 per image',
      supportsImg2Img: true,
    },
    huggingface: {
      hint: 'HuggingFace Inference • FREE text2img only • 100/dag limit',
      supportsImg2Img: false,
    },
    replicate: {
      hint: 'Replicate API • text2img • $0.003 per image',
      supportsImg2Img: false,
    },
    openrouter: {
      hint: 'OpenRouter API • text2img • $0.003 per image',
      supportsImg2Img: false,
    },
    google: {
      hint: 'Google Imagen 4 • text2img • $0.04 per image (expensive)',
      supportsImg2Img: false,
    },
  };

  // Update provider hint
  providerSelect?.addEventListener('change', () => {
    const provider = providerSelect.value;
    const info = providerInfo[provider];
    if (providerHint && info) {
      providerHint.textContent = info.hint;

      if (!info.supportsImg2Img) {
        providerHint.textContent +=
          ' ⚠️ Text-to-image only - geen input image gebruikt';
        providerHint.style.color = '#ff6600';
      } else {
        providerHint.style.color = '#999';
      }
    }
  });

  // Upload area click
  uploadArea?.addEventListener('click', () => fileInput.click());

  // Drag and drop
  uploadArea?.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadArea.classList.add('dragging');
  });

  uploadArea?.addEventListener('dragleave', () => {
    uploadArea.classList.remove('dragging');
  });

  uploadArea?.addEventListener('drop', (e) => {
    e.preventDefault();
    uploadArea.classList.remove('dragging');
    if (e.dataTransfer) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  });

  // File input change
  fileInput?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    if (target.files) {
      handleFiles(Array.from(target.files));
    }
  });

  // Handle selected files
  function handleFiles(files: File[]) {
    // Clean up previous file wrappers
    selectedFiles.forEach((fw) => fw.destroy());

    // Create new file wrappers (max 10 files)
    selectedFiles = files.slice(0, 10).map((file) => {
      const previewUrl = URL.createObjectURL(file);
      return new FileWrapper(file, previewUrl);
    });

    if (selectedFiles.length === 0) return;

    if (batchCounter) {
      batchCounter.textContent = `${selectedFiles.length} bestand(en) geselecteerd`;
    }

    if (previewGrid) {
      previewGrid.innerHTML = '';
    }

    selectedFiles.forEach((fileWrapper, index) => {
      const fileId = `file-${nextFileId++}`;
      const preview = document.createElement('div');
      preview.className = 'result-item';
      preview.id = `preview-${fileId}`;

      preview.innerHTML = `
        <div class="image-container">
          <img 
            src="${fileWrapper.previewUrl}" 
            class="result-image" 
            id="img-${fileId}"
            alt="Preview ${index + 1}" 
            onclick="window.open('${fileWrapper.previewUrl}', '_blank')"
          >
        </div>
        <div id="transform-controls-${fileId}"></div>
        <div class="result-info">
          <strong>${fileWrapper.file.name}</strong><br>
          ${(fileWrapper.file.size / 1024 / 1024).toFixed(2)} MB
        </div>
      `;

      previewGrid?.appendChild(preview);
      fileWrapper.previewElement = preview;

      // Initialize transform controls after image loads
      const img = document.getElementById(`img-${fileId}`) as HTMLImageElement;
      if (img) {
        img.onload = () => {
          fileWrapper.initTransformControls(
            `transform-controls-${fileId}`,
            img
          );
        };
      }
    });

    if (previewContainer) {
      previewContainer.style.display = 'block';
    }
    if (transformForm) {
      transformForm.style.display = 'block';
    }
  }

  // Form submit
  transformForm?.addEventListener('submit', async (e) => {
    e.preventDefault();

    const prompt = (document.getElementById('prompt') as HTMLTextAreaElement)
      .value;
    const provider = providerSelect.value;

    if (selectedFiles.length === 0) {
      showMessage('Geen bestanden geselecteerd', 'error');
      return;
    }

    submitBtn.disabled = true;
    showMessage(
      'Transformatie bezig... Dit kan 10-40 seconden per afbeelding duren.',
      'loading'
    );

    const formData = new FormData();

    // Add files (using transformed blobs if available)
    selectedFiles.forEach((fileWrapper) => {
      formData.append('images', fileWrapper.getFileForUpload());
    });

    formData.append('prompt', prompt);
    formData.append('provider', provider);

    try {
      const response = await (window as any).authenticatedFetch(
        `${API_URL}/api/transform/batch`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        showMessage(
          `✅ ${data.successful} van ${data.total} afbeeldingen succesvol getransformeerd!`,
          'success'
        );
        displayResults(data.results);
      } else {
        showMessage(`Fout: ${data.error}`, 'error');
      }
    } catch (error: any) {
      showMessage(`Fout bij communicatie met API: ${error.message}`, 'error');
    } finally {
      submitBtn.disabled = false;
    }
  });

  // Display results
  async function displayResults(results: any[]) {
    if (!resultsGrid) return;

    resultsGrid.innerHTML = '';

    for (const [index, result] of results.entries()) {
      const item = document.createElement('div');
      item.className = 'result-item';

      if (result.success) {
        // imageUrl is already a data URL, don't prepend API_URL
        let resultUrl = result.imageUrl.startsWith('data:')
          ? result.imageUrl
          : `${API_URL}${result.imageUrl}`;

        // Source image URL (if available)
        let sourceUrl = result.sourceUrl
          ? `${API_URL}${result.sourceUrl}`
          : null;

        // Load authenticated images as blob URLs using the new utility
        try {
          // Load result image if it's a server path
          if (!result.imageUrl.startsWith('data:')) {
            resultUrl = await loadAuthenticatedMedia(resultUrl);
          }

          // Load source image if available and it's a server path
          if (sourceUrl) {
            sourceUrl = await loadAuthenticatedMedia(sourceUrl);
          }
        } catch (error) {
          console.error('Failed to load authenticated images:', error);
          // Continue with direct URLs as fallback
        }

        // Build info lines
        const infoLines = [
          `<strong>✅ ${result.originalName}</strong>`,
          `Model: ${result.metadata.model}`,
        ];

        // Add time if available
        if (result.metadata.generationTime) {
          infoLines.push(
            `Tijd: ${(result.metadata.generationTime / 1000).toFixed(1)}s`
          );
        }

        // Add cost
        infoLines.push(`Kosten: $${result.metadata.cost.toFixed(6)}`);

        // Image size info
        const imageSizeKB = Math.round((resultUrl.length * 0.75) / 1024);
        infoLines.push(`Grootte: ~${imageSizeKB} KB`);

        // Build HTML with source and result
        let imagesHtml = '';
        if (sourceUrl) {
          imagesHtml = `
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin-bottom: 1rem;">
              <div>
                <div style="font-size: 0.8rem; color: #667eea; font-weight: 600; margin-bottom: 0.25rem;">Bron</div>
                <div class="image-container">
                  <img src="${sourceUrl}" class="result-image" alt="Source ${index + 1}" style="margin: 0;" onclick="window.open('${sourceUrl}', '_blank')">
                </div>
              </div>
              <div>
                <div style="font-size: 0.8rem; color: #667eea; font-weight: 600; margin-bottom: 0.25rem;">Resultaat</div>
                <div class="image-container">
                  <img src="${resultUrl}" class="result-image" alt="Result ${index + 1}" style="margin: 0;" onclick="window.open('${resultUrl}', '_blank')">
                </div>
              </div>
            </div>
          `;
        } else {
          imagesHtml = `
            <div class="image-container">
              <img src="${resultUrl}" class="result-image" alt="Result ${index + 1}" onclick="window.open('${resultUrl}', '_blank')">
            </div>
          `;
        }

        item.innerHTML = `
          ${imagesHtml}
          <div class="result-info">
            ${infoLines.join('<br>')}<br>
            <button onclick="window.downloadImage('${resultUrl}', '${result.originalName}')" style="margin-top: 0.5rem; padding: 0.5rem 1rem; cursor: pointer; background: #667eea; color: white; border: none; border-radius: 4px;">
              💾 Download Resultaat
            </button>
            ${
              sourceUrl
                ? `<button onclick="window.open('${sourceUrl}', '_blank')" style="margin-top: 0.5rem; margin-left: 0.5rem; padding: 0.5rem 1rem; cursor: pointer; background: #999; color: white; border: none; border-radius: 4px;">
              👁️ Bekijk Bron
            </button>`
                : ''
            }
          </div>
        `;
      } else {
        item.innerHTML = `
          <div class="result-info">
            <strong>❌ ${result.originalName}</strong><br>
            Fout: ${result.error}
          </div>
        `;
      }

      resultsGrid.appendChild(item);
    }

    if (resultsCard) {
      resultsCard.style.display = 'block';
      resultsCard.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Show message
  function showMessage(text: string, type: string) {
    if (!message) return;

    message.className = type;

    if (type === 'loading') {
      message.innerHTML = `
        <div class="spinner"></div>
        ${text}
      `;
    } else {
      message.textContent = text;
    }
  }
}

// Download image function (global scope for onclick handlers)
(window as any).downloadImage = function (dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename.replace(/\.[^.]+$/, '') + '-transformed.png';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
